/**
 * "Complete your CV" checklist sheet: opened by the bottom-bar setup ring.
 *
 * Surfaces the per-section completeness that `useCvCompleteness` already computes
 * (its `checklistItems`) instead of throwing it away and keeping only the percent.
 * Each row shows done/pending state and deep-links to that section's editor, so
 * tapping the ring is no longer a dead press. Mirrors the public-profile
 * `ProfileChecklistSheet` pattern, on the canonical smooth `BottomSheet` primitive
 * (same one CvListSheets uses).
 */
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import Svg, { Path } from 'react-native-svg';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { colors, spacing } from '@tabbio/tokens';
import { BottomSheet, BottomSheetScrollView } from '../onboarding/BottomSheet';
import { CvChevronIcon } from '../icons/CvManageIcons';
import { useTheme } from '@/hooks/useTheme';
import { useLocale } from '@/hooks/useLocale';
import { useHaptics } from '@/hooks/useHaptics';
import { useCvCompleteness, type CvCompletenessChecklistId } from '@/hooks/useCvCompleteness';
import { buildCvRoute } from '@/lib/cv-target-route';
import { useCVData } from '@/lib/cv-mock-data';

// Each completeness section → its editor route + label key (seeker namespace).
// Routes mirror the CV edit-sections flow (all `edit-*` screens already exist).
const SECTION: Record<CvCompletenessChecklistId, { path: string; labelKey: string }> = {
  header: { path: '/(app)/cv/edit-header', labelKey: 'cv.edit.headerContact' },
  summary: { path: '/(app)/cv/edit-summary', labelKey: 'cv.edit.summary' },
  experience: { path: '/(app)/cv/edit-experience', labelKey: 'cv.edit.experience' },
  education: { path: '/(app)/cv/edit-education', labelKey: 'cv.edit.education' },
  skills: { path: '/(app)/cv/edit-skills', labelKey: 'cv.edit.skills' },
  highlights: { path: '/(app)/cv/edit-highlights', labelKey: 'cv.edit.highlights' },
  certifications: { path: '/(app)/cv/edit-certifications', labelKey: 'cv.edit.certifications' },
  languages: { path: '/(app)/cv/edit-languages', labelKey: 'cv.edit.languages' },
  jobPreferences: { path: '/(app)/cv/edit-job-preferences', labelKey: 'cv.edit.jobPreferences' },
};

function CheckGlyph({ size = 14, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <Path d="M3 7.5L6 10.5L11 4" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function CvSetupSheet({
  visible,
  onClose,
  target,
  buildEditorRoute,
}: {
  visible: boolean;
  onClose: () => void;
  target?: string | null;
  buildEditorRoute?: (path: string) => any;
}) {
  const { theme, isDark } = useTheme();
  const { isRTL } = useLocale();
  const haptics = useHaptics();
  const router = useRouter();
  const { t } = useTranslation('seeker');
  const { isCvScoped } = useCVData();
  const { checklistItems, checklistDone, checklistTotal, completionPercent } = useCvCompleteness();
  const visibleChecklistItems = checklistItems.filter(
    (item) => item.id !== 'jobPreferences' || (!isCvScoped && !buildEditorRoute),
  );

  const flexDirection = isRTL ? ('row-reverse' as const) : ('row' as const);
  const textAlign = isRTL ? ('right' as const) : ('left' as const);

  // Close first, then navigate once the modal VC is actually released
  // (BottomSheet.onAfterClose): a fixed setTimeout races the dismiss
  // animation and, when it fires early, UIKit drops the overlapping
  // dismiss + present and strands a touch-eating transparent modal.
  const afterCloseActionRef = React.useRef<(() => void) | null>(null);
  const handleAfterClose = React.useCallback(() => {
    const action = afterCloseActionRef.current;
    afterCloseActionRef.current = null;
    action?.();
  }, []);
  const openEditor = (path: string) => {
    haptics.trigger('light');
    afterCloseActionRef.current = () =>
      router.push(buildEditorRoute ? buildEditorRoute(path) : buildCvRoute(path, target));
    onClose();
  };

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      onAfterClose={handleAfterClose}
      title={t('cv.setup.title')}
      subtitle={`${checklistDone}/${checklistTotal}`}
      centerHeader
    >
      <View style={styles.body}>
        <View
          style={[
            styles.progressTrack,
            { backgroundColor: isDark ? theme.surfaceSecondary : '#ECECEC' },
          ]}
        >
          <View style={[styles.progressFill, { width: `${completionPercent}%` }]} />
        </View>

        <BottomSheetScrollView style={styles.list} showsVerticalScrollIndicator={false}>
          {visibleChecklistItems.map((item, index) => {
            const meta = SECTION[item.id];
            const isLast = index === visibleChecklistItems.length - 1;
            return (
              <Pressable
                key={item.id}
                onPress={() => openEditor(meta.path)}
                accessibilityRole="button"
                style={({ pressed }) => [
                  styles.row,
                  {
                    flexDirection,
                    borderBottomColor: theme.borderSubtle,
                    borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
                  },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <View
                  style={[
                    styles.indicator,
                    item.completed
                      ? { backgroundColor: colors.primary, borderColor: colors.primary }
                      : { borderColor: theme.border },
                  ]}
                >
                  {item.completed ? <CheckGlyph size={14} color="#fff" /> : null}
                </View>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.label,
                    {
                      color: item.completed ? theme.textTertiary : theme.text,
                      textAlign,
                      textDecorationLine: item.completed ? 'line-through' : 'none',
                      writingDirection: 'auto',
                    },
                  ]}
                >
                  {t(meta.labelKey as never)}
                </Text>
                {!item.countable ? (
                  <Text style={[styles.optionalLabel, { color: theme.textTertiary }]}>
                    {t('cv.setup.optional')}
                  </Text>
                ) : null}
                {/* Base glyph points end-ward (right in LTR); mirror for RTL. */}
                <View style={isRTL ? styles.flip : undefined}>
                  <CvChevronIcon size={14} color={theme.textTertiary} />
                </View>
              </Pressable>
            );
          })}
        </BottomSheetScrollView>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  body: {
    gap: spacing[3],
  },
  progressTrack: {
    height: 6,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: colors.primary,
  },
  list: {
    maxHeight: 380,
  },
  row: {
    minHeight: 52,
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: 12,
  },
  indicator: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 20,
    textTransform: 'capitalize',
  },
  optionalLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  flip: {
    transform: [{ scaleX: -1 }],
  },
});
